import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { ReasonModel } from '@/domain/models/reason.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import type { EstimateFormState } from '@/presentation/hooks/use-estimate-form.js';
import { COMPLEXITY_OPTIONS } from '@/presentation/pages/developments/development-detail/utils/is-superior-estimate.js';
import type { Complexity } from '@/shared/utils/format-status.js';

interface Props {
    functionalEstimateIsComplete: boolean;
    form: EstimateFormState;
    onFieldChange: <K extends keyof EstimateFormState>(key: K, value: EstimateFormState[K]) => void;
    isSuperior: boolean;
    effortIncreaseReasons: ReasonModel[];
    submitting: boolean;
    onSubmit: () => void;
}

export function EstimateForm({ functionalEstimateIsComplete, form, onFieldChange, isSuperior, effortIncreaseReasons, submitting, onSubmit }: Props) {
    const { translate } = useApp();

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    {translate('estimate.technicalEstimate')}
                </Typography>
                {!functionalEstimateIsComplete && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                        {translate('estimate.functionalEstimateRequired')}
                    </Typography>
                )}
                <Stack spacing={2} sx={{ maxWidth: 480 }}>
                    <TextField
                        select
                        label={translate('estimate.complexity')}
                        value={form.complexity}
                        onChange={(e) => onFieldChange('complexity', e.target.value as Complexity)}
                        size="small"
                        fullWidth
                        required
                    >
                        <MenuItem value="" disabled>
                            {translate('estimate.selectComplexity')}
                        </MenuItem>
                        {COMPLEXITY_OPTIONS.map((c) => (
                            <MenuItem key={c} value={c}>
                                {translate(`complexityLabels.${c}`)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        type="number"
                        label={translate('estimate.estimatedHours')}
                        placeholder={translate('estimate.hoursPlaceholder')}
                        value={form.hours}
                        onChange={(e) => onFieldChange('hours', e.target.value)}
                        size="small"
                        fullWidth
                        required
                    />
                    <TextField
                        label={translate('estimate.notes')}
                        placeholder={translate('estimate.notesPlaceholder')}
                        value={form.notes}
                        onChange={(e) => onFieldChange('notes', e.target.value)}
                        size="small"
                        multiline
                        rows={3}
                        fullWidth
                    />
                    {isSuperior ? (
                        <>
                            <TextField
                                select
                                label={translate('estimate.effortIncreaseReason')}
                                value={form.effortIncreaseReasonId}
                                onChange={(e) => onFieldChange('effortIncreaseReasonId', e.target.value)}
                                size="small"
                                fullWidth
                                required
                            >
                                <MenuItem value="" disabled>
                                    {translate('estimate.selectEffortIncreaseReason')}
                                </MenuItem>
                                {effortIncreaseReasons.map((r) => (
                                    <MenuItem key={r.id} value={r.id}>
                                        {r.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label={translate('estimate.effortIncreaseNotes')}
                                placeholder={translate('estimate.effortIncreaseNotesPlaceholder')}
                                value={form.effortIncreaseNotes}
                                onChange={(e) => onFieldChange('effortIncreaseNotes', e.target.value)}
                                size="small"
                                multiline
                                rows={3}
                                fullWidth
                                required
                            />
                            <Button
                                variant="contained"
                                onClick={() => onSubmit()}
                                disabled={
                                    submitting || !functionalEstimateIsComplete || !form.complexity || !form.hours || !form.effortIncreaseReasonId || !form.effortIncreaseNotes.trim()
                                }
                                sx={{ alignSelf: 'flex-start', color: 'white' }}
                            >
                                {submitting ? translate('estimate.submitting') : translate('estimate.sendForApproval')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <TextField
                                type="date"
                                label={translate('estimate.estimatedDelivery')}
                                value={form.delivery}
                                onChange={(e) => onFieldChange('delivery', e.target.value)}
                                size="small"
                                fullWidth
                                required
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => onSubmit()}
                                disabled={submitting || !functionalEstimateIsComplete || !form.complexity || !form.hours || !form.delivery}
                                sx={{ alignSelf: 'flex-start', color: 'white' }}
                            >
                                {submitting ? translate('estimate.submitting') : translate('estimate.sendToDevelopment')}
                            </Button>
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}
